/* ═══ Manualul la pagina cerută, pe telefon și tabletă (29 sep 2026) ═══════════
 *
 * Problema veche (Dan, 29 sep: „am dat pe pagina din manual, aici 145, dar a deschis
 * prima pagină — de mult avem problema asta"): pe telefon, `/api/documents/<id>/view`
 * redirecționează spre PDF-ul brut cu `#page=145`. Android deschide PDF-ul în
 * vizualizatorul lui (Drive / aplicația de PDF), care ignoră fragmentul `#page` —
 * deci pornește mereu de la pagina 1. Pe calculator, vizualizatorul browserului
 * citește fragmentul, acolo merge.
 *
 * Reparația: pe dispozitivele cu ecran tactil, orice link „Manual p.N" din aplicație
 * se deschide în vizualizatorul pdf.js al aplicației (același care merge deja în
 * fișa de lucru) direct la pagina N. Butonul „înapoi" al telefonului îl închide.
 * Pe calculator comportamentul rămâne cel de azi (tab nou).
 */
import { mountWfPdfViewer, type WfPdfViewerHandle } from './pdf-viewer';
import { sim } from './tema';

const RE_LINK = /\/api\/documents\/(\d+)\/view(?:\?(?:[^#]*&)?page=(\d+))?/;

let _viewer: WfPdfViewerHandle | null = null;
let _pagina = 1;
let _total = 0;

function _peTelefonSauTableta(): boolean {
    try {
        if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return true;
    } catch (_e) { /* */ }
    return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
}

function _inchide(): void {
    if (_viewer) { try { _viewer.destroy(); } catch (_e) { /* deja distrus */ } }
    _viewer = null;
    document.getElementById('manual-overlay')?.remove();
    document.removeEventListener('keydown', _laTasta);
}

// Butonul „înapoi" al telefonului: aplicația are deja un paznic (core.ts, `_maintCloseTopLayer`)
// care închide pe rând ferestrele deschise. Fereastra de manual se înscrie la el — o a doua
// ascultare a butonului ar fi afișat și „Apasă din nou pentru a părăsi aplicația".
(window as any).__fmInchideManual = (): boolean => {
    if (!document.getElementById('manual-overlay')) return false;
    _inchide();
    return true;
};

function _laTasta(e: KeyboardEvent): void {
    if (e.key === 'Escape') _inchide();
}

/** Deschide manualul în aplicație, la pagina dată (1 = prima pagină a PDF-ului). */
export function deschideManualLaPagina(docId: number, pagina: number): void {
    // în fișa de lucru există deja vizualizatorul lui — două instanțe s-ar călca pe id-uri
    if (document.getElementById('wf-pdf-canvas')) {
        window.open(`/api/documents/${docId}/view?page=${pagina}`, '_blank', 'noopener');
        return;
    }
    _inchide();
    _pagina = Math.max(1, pagina || 1);
    _total = 0;
    const ov = document.createElement('div');
    ov.id = 'manual-overlay';
    ov.className = 'manual-overlay';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', 'Manual');
    ov.innerHTML = `
        <div class="manual-ov-bar">
            <button type="button" class="manual-ov-btn" data-mo="inchide" aria-label="Închide manualul">${sim('x', '✕')}</button>
            <span class="manual-ov-titlu"><span class="mo-et">Manual — </span>p. <b id="mo-pagina">${_pagina}</b><span id="mo-total"></span></span>
            <button type="button" class="manual-ov-btn" data-mo="inapoi" aria-label="Pagina anterioară">${sim('chevL', '◀')}</button>
            <button type="button" class="manual-ov-btn" data-mo="inainte" aria-label="Pagina următoare">${sim('chev', '▶')}</button>
            <button type="button" class="manual-ov-btn" data-mo="minus" aria-label="Micșorează">${sim('minus', '−')}</button>
            <button type="button" class="manual-ov-btn" data-mo="plus" aria-label="Mărește">${sim('plus', '+')}</button>
            <a class="manual-ov-btn" href="/api/documents/${docId}/file" target="_blank" rel="noopener" aria-label="Deschide PDF-ul întreg" title="Deschide PDF-ul întreg">${sim('external', '↗')}</a>
        </div>
        <div class="manual-ov-host" id="manual-ov-host"></div>`;
    document.body.appendChild(ov);
    const host = ov.querySelector('#manual-ov-host') as HTMLElement;
    _viewer = mountWfPdfViewer({
        container: host,
        docId,
        page: _pagina,
        onPageChange: (p: number, total: number) => {
            _pagina = p; _total = total;
            const el = document.getElementById('mo-pagina'); if (el) el.textContent = String(p);
            const t = document.getElementById('mo-total'); if (t) t.textContent = total ? ` / ${total}` : '';
        },
    });
    ov.addEventListener('click', (e) => {
        const b = (e.target as HTMLElement).closest('[data-mo]') as HTMLElement | null;
        if (!b || !_viewer) return;
        const act = b.dataset.mo;
        if (act === 'inchide') _inchide();
        else if (act === 'inapoi') _viewer.goToPage(Math.max(1, _pagina - 1));
        else if (act === 'inainte') _viewer.goToPage(_total ? Math.min(_total, _pagina + 1) : _pagina + 1);
        else if (act === 'minus') _viewer.zoom(-0.25);
        else if (act === 'plus') _viewer.zoom(0.25);
    });
    document.addEventListener('keydown', _laTasta);
}

let _pornit = false;
/** O singură dată, la pornirea aplicației: prinde clicurile pe linkurile „Manual p.N". */
export function initManualLaPagina(): void {
    if (_pornit) return;
    _pornit = true;
    document.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
        const a = (e.target as HTMLElement | null)?.closest?.('a[href*="/api/documents/"]') as HTMLAnchorElement | null;
        if (!a || !_peTelefonSauTableta()) return;
        const m = (a.getAttribute('href') || '').match(RE_LINK);
        if (!m) return;
        e.preventDefault();
        e.stopPropagation();
        deschideManualLaPagina(parseInt(m[1], 10), m[2] ? parseInt(m[2], 10) : 1);
    }, true);
    // Aceeași problemă pe al doilea drum: 14 butoane deschid manualul cu `window.open(…/view?page=N)`
    // (PDF-ul piesei, diagnosticul, fișa de lucru, chatul…) — pe Android tot la pagina 1 ajungeau
    // (30 sep 2026, turul ferestrelor). Prindem apelul o singură dată, în loc de 14 locuri.
    const deschideOriginal = window.open.bind(window);
    window.open = function (url?: string | URL, target?: string, features?: string): Window | null {
        const u = typeof url === 'string' ? url : (url ? url.toString() : '');
        const m = u.match(RE_LINK);
        if (m && _peTelefonSauTableta() && !document.getElementById('wf-pdf-canvas')) {
            deschideManualLaPagina(parseInt(m[1], 10), m[2] ? parseInt(m[2], 10) : 1);
            return null;
        }
        return deschideOriginal(url as any, target, features);
    } as typeof window.open;
}
